import { makeAutoObservable } from 'mobx';
import { ProjectModel } from '../models/project';
import { RawProject } from '../interfaces/project';
import { createObservableProject } from '../models/project';
import {
  FILES_LIST_TYPE,
  PROJECT_FILTER_TYPE,
  SHOW_COMPLETED_TASK_BY_DATE,
  SHOW_TASK_BY_ORDER,
  SHOW_TASK_GROUPING_TYPE,
  TASK_LIST_TYPE
} from '../../global/constants';
import { getWorkspaceAllProjectsPreferenceData } from '../../helpers/firebaseHelper';
import { rootStore } from '../rootStore';
import { captureException } from '@sentry/react';
import { genericUpsert, genericRemove, GenericUpsertOptions } from '../common';
import { NewTaskFilterInterface, RawProjectFilter } from '../../interfaces/TaskInterface';
import MobxProjectsCacheService from '../../services/mobxProjectsCacheService';
import { ProjectTemplateDetailsInterface } from '../../interfaces/SettingsInterface';
import { CustomerDetailsInterface } from '../../interfaces/CustomerInterface';

export class CreateProjectInput {
  id?: string;
  customerId?: string;
  createdBy?: string;
  startDate?: Date | null;
  name?: string;
  dueDate?: Date | null;
  template?: ProjectTemplateDetailsInterface | null;
  description?: string | null;
  customer?: CustomerDetailsInterface | null;

  constructor() {
    makeAutoObservable(this);
  }

  updateWith(project: ProjectModel) {
    this.id = project.id;
    this.customerId = project.customerId;
    this.createdBy = project.createdById;
    this.name = project.name;
    this.startDate = project.createdOn ? new Date(project.createdOn) : new Date();
    this.dueDate = project.dueDate ? new Date(project.dueDate) : undefined;
    this.description = project.description;
  }

  clear() {
    this.id = undefined;
    this.createdBy = undefined;
    this.name = undefined;
    this.dueDate = undefined;
    this.customerId = undefined;
    this.template = undefined;
    this.customer = undefined;
  }

  setName(name: string) {
    this.name = name;
  }

  setCustomerId(customerId: string) {
    this.customerId = customerId;
  }
  setStartDate(createdOn: Date | null) {
    this.startDate = createdOn;
  }

  setDueDate(dueDate: Date | null) {
    this.dueDate = dueDate;
  }

  setTemplate(template: ProjectTemplateDetailsInterface | null) {
    this.template = template;
  }

  setCustomer(customer: CustomerDetailsInterface | null) {
    this.customer = customer;
  }
}

export class ProjectStore {
  currentProject: ProjectModel | null = null;
  projects: ProjectModel[] = [];
  loading: boolean = true;
  query: string = '';
  sortType: number = PROJECT_FILTER_TYPE.CREATED_DATE;
  projectUserNotIn: ProjectModel[] = [];
  projectFiltersMap: Map<string, NewTaskFilterInterface> = new Map();
  createProjectInput: CreateProjectInput = new CreateProjectInput();
  filesViewListType: number = FILES_LIST_TYPE.GRID;
  uploadFilePercent: number = 0;

  constructor() {
    makeAutoObservable(this);
  }

  get favoriteProjects() {
    return this.projects.filter((project) => project.isFavorite).toSorted((a, b) => a.index - b.index);
  }

  get filteredProjects() {
    const filteredProjects = this.projects.filter((project) =>
      project.name.toLowerCase().includes(this.query.toLowerCase())
    );

    if (this.sortType === PROJECT_FILTER_TYPE.CREATED_DATE) {
      return filteredProjects.toSorted((a, b) => b.createdOn.getTime() - a.createdOn.getTime());
    } else if (this.sortType === PROJECT_FILTER_TYPE.ALPHABETIC) {
      return filteredProjects.toSorted((a, b) => a.name.localeCompare(b.name));
    }

    return filteredProjects;
  }

  getProjectFilter(projectId: string) {
    return this.projectFiltersMap.get(projectId);
  }

  setFilesViewListType(filesViewListType: number) {
    this.filesViewListType = filesViewListType;
  }

  setUploadFilePercent(uploadFilePercent: number) {
    this.uploadFilePercent = uploadFilePercent;
  }

  setProjects(rawProjects: RawProject[]) {
    MobxProjectsCacheService.getInstance().setProjects(rawProjects);
    this.projects = rawProjects.map(createObservableProject);
    this.loading = false;
  }

  upsertProjects(
    rawProjects: RawProject[],
    { excludeFields, currentId }: GenericUpsertOptions<ProjectModel> = {
      excludeFields: ['index', 'taskFilterType', 'taskGroups', 'milestones'],
      currentId: this.currentProject?.id
    }
  ) {
    const { newItems, updatedItems, updatedCurrentItem } = genericUpsert(
      this.projects,
      rawProjects,
      createObservableProject,
      {
        excludeFields,
        currentId
      }
    );

    if (updatedCurrentItem) {
      this.currentProject = updatedCurrentItem;
    }

    MobxProjectsCacheService.getInstance().setProjects(rawProjects);
    return { newItems, updatedItems };
  }

  syncList(newRawProjects: RawProject[], archivedProjects: ProjectModel[]) {
    if (archivedProjects.length > 0) {
      this.removeProjects(archivedProjects.map((project) => project.id));
    }

    if (newRawProjects.length > 0) {
      this.upsertProjects(newRawProjects);
    }
  }

  removeProjects(projectIds: string[]) {
    this.projects = genericRemove(this.projects, projectIds);
    MobxProjectsCacheService.getInstance().removeBulk(projectIds);
  }

  setQuery(query: string) {
    this.query = query;
  }

  setProjectUserNotIn(projectUserNotIn: RawProject[]) {
    this.projectUserNotIn = projectUserNotIn.map(createObservableProject);
  }

  setSortType(sortType: number) {
    this.sortType = sortType;
  }

  setCurrentProject(projectId: string) {
    this.currentProject = this.projects.find((project) => project.id === projectId) || null;
  }

  *updateWithFirebase() {
    try {
      const workspaceId = rootStore.workspaceStore.currentWorkspace?.id;

      if (!workspaceId) {
        return;
      }

      const userProjectsData: Record<string, { fav_index?: number; tasks_filter_type?: RawProjectFilter }> =
        yield getWorkspaceAllProjectsPreferenceData();
      if (!userProjectsData) {
        return;
      }

      const rawProjectFilters = getProjectFiltersMap(userProjectsData);

      for (const project of this.projects) {
        // Update Index
        const index = userProjectsData[project.id]?.fav_index ?? new Date(project?.createdOn).getTime();
        project.index = index;

        // Update Task Filter Type
        const taskFilterType = rawProjectFilters.get(project.id);
        if (taskFilterType) {
          project.taskFilterType = taskFilterType;
        }
      }
    } catch (err) {
      console.error(err);
      captureException(err);
    }
  }

  *updateWithIndexdb() {
    const rawProjects: RawProject[] = yield MobxProjectsCacheService.getInstance().getProjects();
    this.projects = rawProjects.map((rawProject) => createObservableProject(rawProject));
  }

  joinProject(projectId: string) {
    const project = this.projectUserNotIn.find((project) => project.id === projectId);
    if (project) {
      this.projectUserNotIn = this.projectUserNotIn.filter((project) => project.id !== projectId);
      this.projects.push(project);
    }
  }
}

const getProjectFiltersMap = (rawProjectFilters: Record<string, { tasks_filter_type?: RawProjectFilter }>) => {
  if (!rawProjectFilters) {
    return new Map();
  }

  const projectFiltersMap = new Map(
    Object.entries(rawProjectFilters).map(([key, { tasks_filter_type: value }]) => {
      const filter: NewTaskFilterInterface = {
        showSubTask: value?.showSubTask ?? false,
        showCompletedTaskByFilter: value?.showCompletedTask ?? SHOW_COMPLETED_TASK_BY_DATE.LAST_MONTH,
        showRecentlyCompletedTask: value?.showRecentlyCompletedTask ?? true,
        showEmptyGroup: value?.showEmptyGroup ?? true,
        taskViewType: value?.viewType ?? TASK_LIST_TYPE.TASKS,
        groupingStatus: value?.grouping ?? SHOW_TASK_GROUPING_TYPE.DEFAULT,
        orderByStatus: value?.order ?? SHOW_TASK_BY_ORDER.CUSTOM
      };
      return [key, filter];
    })
  );

  return projectFiltersMap;
};
